import { Fix64 } from "../maths/fix64";
import { EventReward, parseReward } from "./events";

/**
 * The fair-mode profile (MP-32) and Daily Road (MP-20), parsed from the bundle's `modes.normalised` and `modes.daily`
 * (balance/src/modes.json; docs/MODES.md §4). The rules are in meta/fairProfile.ts and meta/dailyRoad.ts.
 */

/** A normalised run's profile: these towers owned, every one (the Fortress too) at this level, no gear or research. */
export interface NormalisedData {
  readonly towerLevel: number;
  /** The player's VIP combat bonus still applies on top (owner D1, 2026-10-12); false: the profile is data alone. */
  readonly allowVip: boolean;
  /** Tower ids a normalised run owns (its draft deals only these). */
  readonly towers: readonly string[];
}

export interface DailyRoadData {
  /** The run uses the `normalised` profile (MP-32) instead of the player's towers, levels and gear. */
  readonly normalised: boolean;
  readonly unlockAfterLevel: number;
  /** The campaign layouts the day picks from (inclusive). */
  readonly mapFrom: number;
  readonly mapTo: number;
  readonly stagesMin: number;
  readonly stagesMax: number;
  /** The raiders' power anchor: health × levelBase(powerLevel) / levelBase(map), whatever the map. */
  readonly powerLevel: number;
  /** On top of the anchor. */
  readonly enemyHpMult: Fix64;
  /** The first win of a UTC day pays this (once a day). */
  readonly reward: EventReward;
  /** …and these embers to the live event's track (the event-progress path), when one runs. */
  readonly embers: number;
  /** Days of results the `dailyRoad` save section keeps. */
  readonly historyDays: number;
  readonly stagePoints: number;
  readonly hpPoints: number;
  readonly winPoints: number;
}

/** No data (an old bundle, a test fixture): Daily Road never opens. */
export function emptyDaily(): DailyRoadData {
  return {
    normalised: true, unlockAfterLevel: Number.MAX_SAFE_INTEGER, mapFrom: 1, mapTo: 1, stagesMin: 1, stagesMax: 1, powerLevel: 1, enemyHpMult: Fix64.One,
    reward: parseReward({}), embers: 0, historyDays: 1, stagePoints: 0, hpPoints: 0, winPoints: 0,
  };
}
export function emptyNormalised(): NormalisedData { return { towerLevel: 1, allowVip: false, towers: [] }; }

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Json = any;

function int(o: Json, key: string, where: string, min = 0): number {
  const v = o !== null && typeof o === "object" ? o[key] : undefined;
  if (typeof v === "number" && Number.isInteger(v) && v >= min) return v;
  throw new TypeError(`modes.${where}.${key}: expected an integer >= ${min}, got ${JSON.stringify(v)}`);
}

export function parseNormalised(n: Json): NormalisedData {
  if (n === undefined) return emptyNormalised();
  const towers = n.towers;
  if (!Array.isArray(towers) || !towers.every((t) => typeof t === "string") || towers.length === 0) throw new Error("modes.normalised.towers: tower ids");
  if (n.allowVip !== undefined && typeof n.allowVip !== "boolean") throw new TypeError("modes.normalised.allowVip: true or false");
  return { towerLevel: int(n, "towerLevel", "normalised", 1), allowVip: n.allowVip === true, towers: towers as string[] };
}

export function parseDaily(d: Json): DailyRoadData {
  if (d === undefined) return emptyDaily();
  const n = (k: string, min = 0) => int(d, k, "daily", min);
  const hp = Fix64.parse(String(d.enemyHpMult ?? "1"));
  if (hp.raw <= 0n) throw new RangeError("modes.daily.enemyHpMult: must be positive");
  const out: DailyRoadData = {
    normalised: d.normalised === true, unlockAfterLevel: n("unlockAfterLevel", 1), mapFrom: n("mapFrom", 1), mapTo: n("mapTo", 1),
    stagesMin: n("stagesMin", 1), stagesMax: n("stagesMax", 1), powerLevel: n("powerLevel", 1), enemyHpMult: hp,
    reward: parseReward(d.reward ?? {}), embers: n("embers"), historyDays: n("historyDays", 1),
    stagePoints: n("stagePoints"), hpPoints: n("hpPoints"), winPoints: n("winPoints"),
  };
  if (out.mapTo < out.mapFrom || out.stagesMax < out.stagesMin) throw new Error("modes.daily: mapFrom <= mapTo, stagesMin <= stagesMax");
  return out;
}
